import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {map, Observable, shareReplay} from 'rxjs';
import {environment} from '../../../environments/environment';

/**
 * Lädt die 10-Tage-Vorhersage einer MSWR-Station über den Terminal-Rest-Service
 * (Endpunkt {@code /forecast/{station}}). Der Service reicht die Antwort der
 * Wetterdaten-API unverändert durch; das Auswerten der Messwerte folgt später.
 *
 * Der Umweg über das Backend ist nötig, weil der Browser die Wetterdaten-API in der
 * Cloud nicht direkt aufrufen darf (Local Network Access) und der Zugriffsschlüssel
 * nicht im Client liegen soll.
 *
 * Pro Station wird nur einmal geladen und das Ergebnis geteilt – mehrere Boxen mit
 * derselben Station lösen also nur einen Request aus.
 */
/** Sammlungen der Wetterdaten-API – Gegenstück zum Enum ForecastCollection im Backend. */
export const ForecastCollections = {
  /** 3 Tage in Stundenschritten, Zeitfenster in UTC – Basis für den Heute-Reiter */
  DREI_TAGE_1H: '3tage_1h_UTC_deutschland',
  /** 10 Tage in 6-Stunden-Schritten, Fenster in UTC (seit 29.10.2026 die UTC-Variante der Sammlung) */
  ZEHN_TAGE_6H: '10tage_6h_UTC_deutschland'
} as const;

/**
 * Wind-Werte einer Box der Karte "Wind in Bft." – Gegenstück zum Record WindWerte
 * im Backend. Mittelwind und Böen kommen bereits in Beaufort, die Richtung als
 * Zahl 1..16 für den Windpfeil (1 = Wind aus Süd, je Schritt 22,5 Grad im Uhrzeigersinn,
 * Tabelle der Redaktion – siehe ImageComponent.WIND_RICHTUNGEN).
 */
export interface WindValues {
  mittel: number | null;
  boeen: number | null;
  richtung: number | null;
  richtungName: string | null;
  grad: number | null;
  einheit: string | null;
}

/**
 * Woran eine Box ihren Wert festmacht. Die NRW-Karten tragen eine Region, die das Backend
 * über seine Stations-Tabellen auflöst. Die Lokalzeit-Karten tragen keine Region, sondern
 * direkt ihre Station ({@code ImageBox.station}, aus der Stationsdatei
 * {@code wdr-lokalzeiten-stations.json} des Backends) – ihre Ortsnamen ("Heinsberg", "Kleve") sollen sich nie mit den
 * Regionsnamen der NRW-Karten treffen. Ist beides gesetzt, gilt die Region.
 */
export interface BoxOrt {
  region?: string;
  station?: string;
}

/** Query-Parameter für einen BoxOrt: region=… bzw. station=…, dazu die Reihe. */
function ortParameter(ort: BoxOrt, reihe: string): HttpParams {
  const params = ort.region
    ? new HttpParams().set('region', ort.region)
    : new HttpParams().set('station', ort.station ?? '');
  return params.set('reihe', reihe);
}

/** Cache-Schlüssel eines BoxOrts – Region und Station dürfen nicht kollidieren. */
function ortSchluessel(ort: BoxOrt): string {
  return ort.region ? `r:${ort.region}` : `s:${ort.station}`;
}

@Injectable({
  providedIn: 'root'
})
export class StationForecastService {

  private readonly apiUrl = environment.terminalApiUrl;
  private readonly cache = new Map<string, Observable<unknown>>();
  private readonly symbolCache = new Map<string, Observable<number | null>>();
  private readonly temperatureCache = new Map<string, Observable<number | null>>();
  private readonly windCache = new Map<string, Observable<WindValues | null>>();

  constructor(private http: HttpClient) {
  }

  /**
   * Vorhersage einer Station aus einer Sammlung (Rohdaten).
   *
   * @param station  Stations-Kennung, z.B. 'V_MSWR_L_H744XX'
   * @param sammlung Sammlung der Wetterdaten-API; für den Heute-Reiter '3tage_1h_deutschland'
   */
  fetch(station: string, sammlung: string = ForecastCollections.DREI_TAGE_1H): Observable<unknown> {
    const key = `${sammlung}/${station}`;
    const cached = this.cache.get(key);
    if (cached) {
      return cached;
    }
    const request$ = this.http
      .get(`${this.apiUrl}/forecast/${encodeURIComponent(sammlung)}/${encodeURIComponent(station)}`)
      .pipe(shareReplay({bufferSize: 1, refCount: false}));
    this.cache.set(key, request$);
    return request$;
  }

  /**
   * Verwirft die zwischengespeicherten Antworten, sodass der nächste Abruf wieder
   * beim Server landet. Wird vom Button "MSWR-MOS laden" genutzt – ohne das käme
   * nach dem ersten Laden nur der gemerkte Wert zurück.
   */
  clearCache(): void {
    this.cache.clear();
    this.symbolCache.clear();
    this.temperatureCache.clear();
    this.windCache.clear();
  }

  /**
   * Wetter-Symbol einer Box: Region und Reihe gehen an das Backend, das daraus
   * Station, Sammlung, Tag und Zeitspanne bestimmt und den Symbol-Code liefert.
   *
   * Antwortet der Server mit 204 (Reihe noch nicht gemappt), liefert der Aufruf null.
   *
   * Region und Reihe gehen als Query-Parameter mit, nicht im Pfad: fünf Regionsnamen
   * enthalten einen Schrägstrich ('Ostwestfalen-Lippe / OWL'), und ein kodierter
   * Schrägstrich (%2F) in einem Pfadsegment wird serverseitig von Tomcat mit 400
   * abgewiesen, bevor der Endpunkt überhaupt läuft.
   *
   * @param ort   Region (z.B. 'Rheinland') oder Station einer Lokalzeit-Box
   * @param reihe Überschrift der Karten-Reihe, z.B. 'HEUTE: Vormittag'
   */
  fetchSymbol(ort: BoxOrt, reihe: string): Observable<number | null> {
    const key = `symbol|${ortSchluessel(ort)}|${reihe}`;
    const cached = this.symbolCache.get(key);
    if (cached) {
      return cached;
    }
    const params = ortParameter(ort, reihe);
    const request$ = this.http
      .get(`${this.apiUrl}/forecast/symbol`, {params, responseType: 'text'})
      .pipe(
        map(text => (text === null || text === '' ? null : Number(text))),
        map(wert => (wert === null || isNaN(wert) ? null : wert)),
        shareReplay({bufferSize: 1, refCount: false})
      );
    this.symbolCache.set(key, request$);
    return request$;
  }

  /**
   * Temperatur einer Box: Region und Reihe gehen an das Backend, das daraus Station,
   * Sammlung, Tag und Zeitspanne bestimmt und {@code luftTemperaturMaximum} liefert.
   *
   * Antwortet der Server mit 204 (Reihe nicht gemappt oder kein Wert), liefert der
   * Aufruf null.
   *
   * @param ort   Region (z.B. 'Kölner Bucht') oder Station einer Lokalzeit-Box
   * @param reihe Überschrift der Karten-Reihe, z.B. 'HEUTE: Vormittag'
   */
  fetchTemperature(ort: BoxOrt, reihe: string): Observable<number | null> {
    const key = `temp|${ortSchluessel(ort)}|${reihe}`;
    const cached = this.temperatureCache.get(key);
    if (cached) {
      return cached;
    }
    const params = ortParameter(ort, reihe);
    const request$ = this.http
      .get(`${this.apiUrl}/forecast/temperature`, {params, responseType: 'text'})
      .pipe(
        map(text => (text === null || text === '' ? null : Number(text))),
        map(wert => (wert === null || isNaN(wert) ? null : wert)),
        shareReplay({bufferSize: 1, refCount: false})
      );
    this.temperatureCache.set(key, request$);
    return request$;
  }

  /**
   * Wind-Werte einer Box: Region und Reihe gehen an das Backend, das daraus Station,
   * Sammlung, Tag und Zeitspanne bestimmt und Mittelwind, Böen und Richtung liefert.
   *
   * Eigener Endpunkt neben {@code forecast/symbol}, weil die Wind-Karte ihre eigene
   * Stations-Tabelle hat: derselbe Regionsname kann für Wettersymbol und Wind auf
   * verschiedene Stationen zeigen.
   *
   * Antwortet der Server mit 204 (Reihe nicht gemappt oder keine Werte), liefert der
   * Aufruf null.
   *
   * @param ort   Region (z.B. 'Ruhrgebiet') oder Station der Wind-Box einer Lokalzeit-Karte
   * @param reihe Überschrift der Karten-Reihe, z.B. 'HEUTE: Mittag'
   */
  fetchWind(ort: BoxOrt, reihe: string): Observable<WindValues | null> {
    const key = `wind|${ortSchluessel(ort)}|${reihe}`;
    const cached = this.windCache.get(key);
    if (cached) {
      return cached;
    }
    const params = ortParameter(ort, reihe);
    const request$ = this.http
      .get<WindValues>(`${this.apiUrl}/forecast/wind`, {params})
      .pipe(
        // 204 kommt als leerer Body an und wird zu null
        map(werte => (werte ? werte : null)),
        shareReplay({bufferSize: 1, refCount: false})
      );
    this.windCache.set(key, request$);
    return request$;
  }
}
